import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('memora_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('memora_token');
      localStorage.removeItem('memora_user');
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  requestMagicLink: (email) => api.post('/auth/magic-link', { email }),
  verifyMagicLink: (token) => api.post('/auth/verify', { token }),
  sponsorLogin: (email, password) => api.post('/auth/sponsor/login', { email, password }),
  sponsorRegister: (company_name, email, password) => api.post('/auth/sponsor/register', { company_name, email, password }),
  getMe: () => api.get('/auth/me'),
};

// Tokens
export const tokenApi = {
  getToken: (tokenId) => api.get(`/tokens/${tokenId}`),
  activateToken: (tokenId, location = {}) => api.post('/tokens/activate', { token_id: tokenId, ...location }),
  getUserArtifacts: () => api.get('/tokens/user/artifacts'),
  getSponsorInfo: (tokenId, moduleType) => api.get(`/tokens/${tokenId}/sponsor-info`, { params: { module_type: moduleType } }),
  updateRemarks: (tokenId, remarks) => api.put(`/tokens/${tokenId}/remarks`, { remarks }),
};

// Campaign settings
export const campaignSettingsApi = {
  getSettings: (campaignId) => api.get(`/campaigns/${campaignId}/settings`),
  updateSettings: (campaignId, data) => api.put(`/campaigns/${campaignId}/settings`, data),
};

// Notes
export const notesApi = {
  createNote: (tokenId, imageBase64, tags = []) => api.post('/notes', { token_id: tokenId, image_base64: imageBase64, tags }),
  getNotes: (tokenId, skip = 0, limit = 50) => api.get(`/notes/${tokenId}`, { params: { skip, limit } }),
  getStats: (tokenId) => api.get(`/notes/${tokenId}/stats`),
};

// Travel
export const travelApi = {
  createDocument: (tokenId, fileBase64, documentType, tripName) => 
    api.post('/travel/documents', { token_id: tokenId, file_base64: fileBase64, document_type: documentType, trip_name: tripName }),
  getDocuments: (tokenId, tripName) => api.get(`/travel/documents/${tokenId}`, { params: { trip_name: tripName } }),
  getDocumentFile: (docId) => api.get(`/travel/document/${docId}`),
  createExpense: (tokenId, merchantName, amount, category, tripName, receiptBase64) =>
    api.post('/travel/expenses', { token_id: tokenId, merchant_name: merchantName, amount, category, trip_name: tripName, receipt_base64: receiptBase64 }),
  getExpenses: (tokenId, tripName) => api.get(`/travel/expenses/${tokenId}`, { params: { trip_name: tripName } }),
  getExpenseSummary: (tokenId, tripName) => api.get(`/travel/expenses/${tokenId}/summary`, { params: { trip_name: tripName } }),
  endTrip: (tokenId, tripName) => api.delete(`/travel/trip/${tokenId}/${tripName}`),
};

// Home Hub
export const homeApi = {
  // Calendar
  createEvent: (tokenId, title, description, eventDate, eventTime, familyMember) =>
    api.post('/home/calendar', { token_id: tokenId, title, description, event_date: eventDate, event_time: eventTime, family_member: familyMember }),
  getEvents: (tokenId, startDate, endDate) => api.get(`/home/calendar/${tokenId}`, { params: { start_date: startDate, end_date: endDate } }),
  deleteEvent: (eventId) => api.delete(`/home/calendar/${eventId}`),
  
  // Groceries
  createGroceryItem: (tokenId, name, quantity = 1) => api.post('/home/groceries', { token_id: tokenId, name, quantity }),
  getGroceryItems: (tokenId) => api.get(`/home/groceries/${tokenId}`),
  toggleGroceryItem: (itemId, isChecked) => api.patch(`/home/groceries/${itemId}`, null, { params: { is_checked: isChecked } }),
  deleteGroceryItem: (itemId) => api.delete(`/home/groceries/${itemId}`),
  
  // Medications
  createMedication: (tokenId, name, dosage, schedule, familyMember) =>
    api.post('/home/medications', { token_id: tokenId, name, dosage, schedule, family_member: familyMember }),
  getMedications: (tokenId) => api.get(`/home/medications/${tokenId}`),
  deleteMedication: (medicationId) => api.delete(`/home/medications/${medicationId}`),
  
  // Sticky Notes
  createStickyNote: (tokenId, content, color = 'yellow') => api.post('/home/sticky-notes', { token_id: tokenId, content, color }),
  getStickyNotes: (tokenId) => api.get(`/home/sticky-notes/${tokenId}`),
  deleteStickyNote: (noteId) => api.delete(`/home/sticky-notes/${noteId}`),
};

// Sponsor
export const sponsorApi = {
  getCampaigns: () => api.get('/sponsor/campaigns'),
  createCampaign: (name, artifactType, startDate, endDate) =>
    api.post('/sponsor/campaigns', { name, artifact_type: artifactType, start_date: startDate, end_date: endDate }),
  getCampaign: (campaignId) => api.get(`/sponsor/campaigns/${campaignId}`),
  getCampaignAnalytics: (campaignId) => api.get(`/sponsor/campaigns/${campaignId}/analytics`),
  getCampaignHeatmap: (campaignId) => api.get(`/sponsor/campaigns/${campaignId}/heatmap`),
  createBatch: (campaignId, batchName, tokenCount) =>
    api.post('/sponsor/batches', { campaign_id: campaignId, batch_name: batchName, token_count: tokenCount }),
  getBatches: (campaignId) => api.get(`/sponsor/batches/${campaignId}`),
  getTokens: (campaignId, batchId, status, skip, limit) =>
    api.get('/admin/tokens', { params: { campaign_id: campaignId, batch_id: batchId, status, skip, limit } }),
};

// CTA
export const ctaApi = {
  createCTA: (campaignId, title, link, sortOrder = 0, isActive = true, openInNewTab = true, moduleVisibility = ['notes', 'travel', 'home', 'car', 'finance']) => 
    api.post('/cta', { campaign_id: campaignId, title, link, sort_order: sortOrder, is_active: isActive, open_in_new_tab: openInNewTab, module_visibility: moduleVisibility }),
  getCTAs: (campaignId) => api.get(`/cta/${campaignId}`),
  updateCTA: (ctaId, data) => api.put(`/cta/${ctaId}`, data),
  deleteCTA: (ctaId) => api.delete(`/cta/${ctaId}`),
  recordClick: (ctaId, tokenId, moduleType) => api.post(`/cta/${ctaId}/click`, null, { params: { token_id: tokenId, module_type: moduleType } }),
};

// Reports
export const reportApi = {
  createShareableReport: (campaignId, expirationHours = 72, viewOnly = true) =>
    api.post('/reports/share', { campaign_id: campaignId, expiration_hours: expirationHours, view_only: viewOnly }),
  getShareableReport: (reportToken) => api.get(`/reports/${reportToken}`),
};

// Car Diary
export const carApi = {
  createVehicle: (data) => api.post('/car/vehicle', data),
  getVehicle: (tokenId) => api.get(`/car/vehicle/${tokenId}`),
  updateVehicle: (vehicleId, data) => api.put(`/car/vehicle/${vehicleId}`, data),
  getDashboard: (tokenId) => api.get(`/car/dashboard/${tokenId}`),
  createService: (data) => api.post('/car/services', data),
  getServices: (tokenId) => api.get(`/car/services/${tokenId}`),
  deleteService: (serviceId) => api.delete(`/car/services/${serviceId}`),
  createFuelLog: (data) => api.post('/car/fuel', data),
  getFuelLogs: (tokenId) => api.get(`/car/fuel/${tokenId}`),
  getFuelStats: (tokenId) => api.get(`/car/fuel/${tokenId}/stats`),
  deleteFuelLog: (fuelId) => api.delete(`/car/fuel/${fuelId}`),
  createTrip: (data) => api.post('/car/trips', data),
  getTrips: (tokenId) => api.get(`/car/trips/${tokenId}`),
  deleteTrip: (tripId) => api.delete(`/car/trips/${tripId}`),
  createIncident: (data) => api.post('/car/incidents', data),
  getIncidents: (tokenId) => api.get(`/car/incidents/${tokenId}`),
  deleteIncident: (incidentId) => api.delete(`/car/incidents/${incidentId}`),
};

// Wealth Compass (Finance)
export const financeApi = {
  getDashboard: (tokenId) => api.get(`/finance/dashboard/${tokenId}`),
  createAsset: (data) => api.post('/finance/assets', data),
  getAssets: (tokenId) => api.get(`/finance/assets/${tokenId}`),
  updateAsset: (assetId, data) => api.put(`/finance/assets/${assetId}`, data),
  deleteAsset: (assetId) => api.delete(`/finance/assets/${assetId}`),
  createLiability: (data) => api.post('/finance/liabilities', data),
  getLiabilities: (tokenId) => api.get(`/finance/liabilities/${tokenId}`),
  updateLiability: (liabilityId, data) => api.put(`/finance/liabilities/${liabilityId}`, data),
  deleteLiability: (liabilityId) => api.delete(`/finance/liabilities/${liabilityId}`),
  createIncome: (data) => api.post('/finance/income', data),
  getIncomeStreams: (tokenId) => api.get(`/finance/income/${tokenId}`),
  updateIncome: (incomeId, data) => api.put(`/finance/income/${incomeId}`, data),
  deleteIncome: (incomeId) => api.delete(`/finance/income/${incomeId}`),
  createProjection: (data) => api.post('/finance/projections', data),
  getProjections: (tokenId) => api.get(`/finance/projections/${tokenId}`),
  deleteProjection: (projectionId) => api.delete(`/finance/projections/${projectionId}`),
};

// Analytics
export const analyticsApi = {
  logEvent: (tokenId, eventType, location = {}, metadata = {}) =>
    api.post('/analytics/event', { token_id: tokenId, event_type: eventType, ...location, metadata }),
};

// Admin
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getCampaigns: () => api.get('/admin/campaigns'),
  getSponsors: () => api.get('/admin/sponsors'),
  getUsers: (search, skip, limit) => api.get('/admin/users', { params: { search, skip, limit } }),
  getTokens: (campaignId, batchId, status, skip, limit) =>
    api.get('/admin/tokens', { params: { campaign_id: campaignId, batch_id: batchId, status, skip, limit } }),
  exportTokens: (campaignId) => api.get(`/admin/tokens/export/${campaignId}`),
  seedDemoData: () => api.post('/admin/seed-demo'),
  resetDemoData: () => api.delete('/admin/reset-demo'),
  // New admin endpoints
  createCampaign: (name, sponsorId, artifactType, tokenPrefix, tokenQuantity, startDate, endDate) =>
    api.post('/admin/campaigns', { name, sponsor_id: sponsorId, artifact_type: artifactType, token_prefix: tokenPrefix, token_quantity: tokenQuantity, start_date: startDate, end_date: endDate }),
  getCampaignDetails: (campaignId) => api.get(`/admin/campaigns/${campaignId}/details`),
  getCampaignTokens: (campaignId, status, search, skip, limit) =>
    api.get(`/admin/campaigns/${campaignId}/tokens`, { params: { status, search, skip, limit } }),
  generateTokens: (campaignId, tokenQuantity, moduleType, prefix) =>
    api.post('/admin/tokens/generate', { campaign_id: campaignId, token_quantity: tokenQuantity, module_type: moduleType, prefix }),
  assignUserToCampaign: (campaignId, userEmail) =>
    api.post('/admin/campaigns/assign-user', { campaign_id: campaignId, user_email: userEmail }),
  updateTokenStatus: (tokenId, status) =>
    api.patch('/admin/tokens/status', { token_id: tokenId, status }),
  deleteCampaign: (campaignId) => api.delete(`/admin/campaigns/${campaignId}`),
  getDemoTokens: () => api.get('/admin/demo-tokens'),
  globalSearch: (query, type) => api.get('/admin/search', { params: { q: query, type } }),
};

export default api;
